'use client';

import * as React from 'react';
import dynamicIconImports from 'lucide-react/dynamicIconImports';
import { useTranslation } from 'react-i18next';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { ChevronDown, X } from 'lucide-react';
import { Input } from '@/components/ui/input';

type LucideIconName = keyof typeof dynamicIconImports;

export function LucideLazyIcon({
  name,
  ...rest
}: { name: string } & React.SVGProps<SVGSVGElement>) {
  const importer = (dynamicIconImports as Record<string, any>)[name];
  if (typeof importer !== 'function') return null;

  const Icon = React.useMemo(() => React.lazy(importer), [importer]);

  return (
    <React.Suspense fallback={<span className="" />}>
      <Icon {...rest} />
    </React.Suspense>
  );
}

const FR_SYNONYMS: Record<string, string[]> = {
  // personnes / compte
  user: ['utilisateur', 'profil', 'personne', 'compte', 'membre', 'client'],
  users: ['utilisateurs', 'groupe', 'équipe', 'communauté', 'membres'],
  contact: ['contact', 'annuaire'],
  id: ['identité', 'carte', 'badge'],

  // navigation / maison
  home: ['accueil', 'maison', 'domicile'],
  menu: ['menu', 'navigation'],
  search: ['recherche', 'chercher', 'loupe'],
  filter: ['filtre', 'filtrer'],
  sort: ['trier', 'tri'],
  list: ['liste'],
  grid: ['grille'],
  layout: ['mise en page', 'layout'],

  // actions
  plus: ['ajouter', 'plus', 'nouveau', 'créer'],
  add: ['ajouter', 'ajout', 'créer'],
  minus: ['retirer', 'supprimer', 'moins'],
  remove: ['retirer', 'supprimer'],
  delete: ['supprimer', 'effacer'],
  trash: ['supprimer', 'poubelle'],
  edit: ['modifier', 'édition', 'éditer'],
  pen: ['stylo', 'modifier'],
  save: ['enregistrer', 'sauvegarder'],
  upload: ['upload', 'téléverser', 'importer'],
  download: ['télécharger', 'exporter'],
  share: ['partager'],
  copy: ['copier', 'dupliquer'],
  link: ['lien', 'attacher'],
  unlink: ['détacher', 'supprimer lien'],
  refresh: ['rafraîchir', 'recharger'],
  rotate: ['tourner', 'rotation'],
  move: ['déplacer'],
  drag: ['glisser'],
  close: ['fermer'],
  x: ['fermer'],
  check: ['valider', 'ok', 'succès', 'confirmé'],
  alert: ['alerte', 'attention'],
  info: ['info', 'information'],
  help: ['aide', 'support'],

  // communication
  mail: ['email', 'mail', 'courrier'],
  message: ['message', 'discussion', 'chat'],
  messages: ['messages', 'discussion', 'chat'],
  chat: ['chat', 'conversation', 'discussion'],
  phone: ['téléphone', 'appel'],
  call: ['appel', 'téléphoner'],
  send: ['envoyer'],

  // sécurité
  lock: ['verrou', 'cadenas', 'sécurité'],
  unlock: ['déverrouiller', 'ouvrir', 'accès'],
  shield: ['protection', 'sécurité', 'bouclier'],
  key: ['clé', 'sécurité'],
  password: ['mot de passe', 'sécurité'],

  // temps / organisation
  calendar: ['calendrier', 'agenda', 'planning', 'date'],
  clock: ['horloge', 'heure', 'temps'],
  time: ['temps', 'heure'],
  timer: ['minuteur', 'compte à rebours'],
  alarm: ['alarme', 'réveil'],
  bell: ['notification', 'cloche', 'alerte'],

  // ====== E-COMMERCE / VENTE ======
  shop: ['boutique', 'shop', 'magasin', 'commerce'],
  store: ['boutique', 'magasin', 'commerce'],
  shopping: ['shopping', 'achat', 'boutique'],
  cart: ['panier', 'achat', 'commande', 'boutique'],
  basket: ['panier'],
  bag: ['sac', 'panier'],
  tag: ['étiquette', 'tag', 'prix', 'promo'],
  tags: ['étiquettes', 'tags', 'prix', 'promo'],
  sale: ['vente', 'soldes', 'promotion', 'promo'],
  percent: ['pourcentage', 'réduction', 'promo', 'remise'],
  discount: ['réduction', 'remise', 'promo'],
  coupon: ['coupon', 'code promo', 'promo'],
  ticket: ['ticket', 'bon', 'coupon'],
  gift: ['cadeau', 'offre', 'bonus'],
  box: ['boîte', 'colis', 'pack'],
  package: ['colis', 'pack', 'paquet'],
  parcel: ['colis', 'livraison'],
  truck: ['livraison', 'transport', 'expédition'],
  delivery: ['livraison', 'expédition'],
  ship: ['livrer', 'expédier', 'livraison'],
  shipping: ['livraison', 'expédition', 'frais de port'],
  warehouse: ['entrepôt', 'stock'],
  stock: ['stock', 'inventaire'],
  inventory: ['inventaire', 'stock'],
  barcode: ['code-barres', 'barcode'],
  qr: ['qr', 'qrcode', 'code qr'],
  scan: ['scanner', 'scan'],
  receipt: ['reçu', 'facture', 'ticket'],
  invoice: ['facture', 'devis', 'billing'],
  bill: ['facture', 'note'],
  clipboard: ['liste', 'checklist', 'devis', 'commande'],
  order: ['commande'],
  checkout: ['paiement', 'checkout', 'caisse'],
  cartcheckout: ['paiement', 'caisse'],

  // ====== PAIEMENT / ARGENT ======
  credit: ['carte', 'paiement', 'crédit'],
  card: ['carte', 'paiement', 'badge'],
  wallet: ['portefeuille', 'paiement'],
  bank: ['banque'],
  cash: ['argent', 'cash', 'espèces'],
  money: ['argent', 'paiement'],
  coin: ['pièce', 'crypto', 'argent'],
  coins: ['pièces', 'crypto', 'argent'],
  dollar: ['prix', 'argent', 'dollar'],
  euro: ['prix', 'argent', 'euro'],
  pound: ['prix', 'argent', 'livre sterling'],
  bitcoin: ['bitcoin', 'crypto', 'cryptomonnaie'],
  crypto: ['crypto', 'cryptomonnaie'],
  pay: ['payer', 'paiement'],
  payment: ['paiement', 'payer'],
  price: ['prix', 'tarif'],
  pricing: ['tarifs', 'pricing', 'prix'],
  chart: ['graphique', 'courbe', 'stats'],
  trend: ['tendance', 'évolution'],

  // ====== PREMIUM / COURONNE / BADGES ======
  crown: ['couronne', 'premium', 'vip', 'pro', 'elite', 'royal'],
  badge: ['badge', 'récompense', 'statut', 'niveau'],
  award: ['récompense', 'prix', 'trophée'],
  trophy: ['trophée', 'récompense'],
  star: ['étoile', 'favori', 'favoris', 'premium'],
  sparkles: ['premium', 'magique', 'nouveau'],
  diamond: ['diamant', 'premium', 'vip'],
  gem: ['gemme', 'premium', 'vip'],
  shieldcheck: ['certifié', 'vérifié', 'sécurisé'],
  verify: ['vérifier', 'vérifié', 'certifié'],

  // fichiers / média
  file: ['fichier', 'document'],
  files: ['fichiers', 'documents'],
  folder: ['dossier'],
  image: ['image', 'photo'],
  video: ['vidéo'],
  music: ['musique'],
  camera: ['caméra', 'appareil photo'],
  mic: ['micro'],

  // lieux
  map: ['carte', 'plan'],
  pin: ['position', 'localisation', 'épingle'],
  location: ['localisation', 'position'],
  globe: ['monde', 'internet'],

  // états / UI
  settings: ['paramètres', 'réglages', 'configuration'],
  gear: ['paramètres', 'réglages'],
  loader: ['chargement', 'loading'],
  loading: ['chargement', 'loading'],
  heart: ['coeur', 'favori', 'favoris', 'aimer'],
};

function normalize(s: string) {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function splitIconNameToTokens(name: string): string[] {
  const parts = name.match(/[A-Z]+(?=[A-Z][a-z])|[A-Z]?[a-z]+|\d+/g) ?? [];
  return parts.map((p) => p.toLowerCase());
}

function buildLucideFrenchTags(allNames: string[]) {
  const tagsByName: Record<string, string[]> = {};

  for (const iconName of allNames) {
    const tokens = splitIconNameToTokens(iconName);

    const tags = new Set<string>();

    tags.add(normalize(iconName));

    const has = (t: string) => tokens.includes(t);

    for (const t of tokens) tags.add(normalize(t));

    for (const t of tokens) {
      const syns = FR_SYNONYMS[t];
      if (syns) syns.forEach((s) => tags.add(normalize(s)));
    }

    // Panier / ajout / retrait
    if (
      (has('shopping') || has('cart') || has('bag') || has('basket')) &&
      (has('plus') || has('add'))
    ) {
      tags.add(normalize('ajouter au panier'));
      tags.add(normalize('ajout panier'));
    }
    if (
      (has('shopping') || has('cart') || has('bag') || has('basket')) &&
      (has('minus') || has('remove') || has('delete'))
    ) {
      tags.add(normalize('retirer du panier'));
      tags.add(normalize('supprimer du panier'));
    }

    // Achat / paiement
    if (
      (has('cart') || has('shopping') || has('store') || has('shop')) &&
      (has('check') || has('checkout'))
    ) {
      tags.add(normalize('paiement'));
      tags.add(normalize('passer commande'));
      tags.add(normalize('caisse'));
      tags.add(normalize('checkout'));
    }
    if (
      (has('credit') || has('card') || has('wallet')) &&
      (has('check') || has('lock') || has('shield'))
    ) {
      tags.add(normalize('paiement sécurisé'));
      tags.add(normalize('transaction sécurisée'));
    }

    // Prix / promo / réduction
    if ((has('tag') || has('tags')) && (has('percent') || has('sale'))) {
      tags.add(normalize('promotion'));
      tags.add(normalize('réduction'));
      tags.add(normalize('prix réduit'));
      tags.add(normalize('soldes'));
    }
    if (has('badge') && has('percent')) {
      tags.add(normalize('réduction'));
      tags.add(normalize('promo'));
      tags.add(normalize('pourcentage'));
    }

    // Facture / reçu
    if (
      (has('receipt') || has('invoice') || has('bill')) &&
      (has('check') || has('file') || has('clipboard'))
    ) {
      tags.add(normalize('facturation'));
      tags.add(normalize('reçu'));
      tags.add(normalize('facture'));
      tags.add(normalize('commande'));
    }

    // Livraison / colis
    if (
      (has('truck') || has('package') || has('box') || has('parcel')) &&
      (has('clock') || has('time'))
    ) {
      tags.add(normalize('suivi livraison'));
      tags.add(normalize('délai livraison'));
    }
    if (has('truck') || has('package') || has('box') || has('parcel')) {
      tags.add(normalize('livraison'));
      tags.add(normalize('expédition'));
      tags.add(normalize('colis'));
      tags.add(normalize('shipping'));
    }

    // Stock / inventaire
    if ((has('warehouse') || has('box') || has('package')) && (has('list') || has('clipboard'))) {
      tags.add(normalize('inventaire'));
      tags.add(normalize('gestion stock'));
    }

    // QR / scan / code
    if ((has('qr') || has('scan') || has('barcode')) && (has('search') || has('check'))) {
      tags.add(normalize('scanner'));
      tags.add(normalize('scan produit'));
      tags.add(normalize('code'));
    }

    // Premium / VIP / Couronne / badges
    if (has('crown')) {
      tags.add(normalize('premium'));
      tags.add(normalize('vip'));
      tags.add(normalize('abonnement'));
      tags.add(normalize('pro'));
      tags.add(normalize('élite'));
    }
    if (
      (has('crown') || has('badge') || has('award') || has('trophy') || has('star')) &&
      has('check')
    ) {
      tags.add(normalize('certifié'));
      tags.add(normalize('vérifié'));
      tags.add(normalize('premium vérifié'));
    }
    if ((has('diamond') || has('gem') || has('sparkles')) && (has('crown') || has('star'))) {
      tags.add(normalize('premium'));
      tags.add(normalize('vip'));
      tags.add(normalize('offre premium'));
    }

    if (
      (has('dollar') ||
        has('euro') ||
        has('coin') ||
        has('coins') ||
        has('wallet') ||
        has('bank')) &&
      has('arrow')
    ) {
      tags.add(normalize('transaction'));
      tags.add(normalize('paiement'));
      tags.add(normalize('virement'));
    }
    if (has('dollar') || has('euro') || has('coin') || has('coins')) {
      tags.add(normalize('argent'));
      tags.add(normalize('prix'));
      tags.add(normalize('monnaie'));
    }

    if (tokens.includes('user') && tokens.includes('plus'))
      tags.add(normalize('ajouter utilisateur'));
    if (tokens.includes('user') && tokens.includes('minus'))
      tags.add(normalize('retirer utilisateur'));
    if (tokens.includes('lock') && tokens.includes('open')) tags.add(normalize('déverrouiller'));

    tagsByName[iconName] = Array.from(tags);
  }

  return tagsByName;
}

export function LucideIconPopoverPicker(props: {
  value?: LucideIconName | '';
  onChange: (name: LucideIconName | '' | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  const { value: rawValue = '', onChange, disabled = false } = props;
  const { t } = useTranslation('createWebinar');
  const value = rawValue || '';

  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');

  const allNames = React.useMemo(() => Object.keys(dynamicIconImports) as LucideIconName[], []);

  const frTagsByName = React.useMemo(() => {
    return buildLucideFrenchTags(allNames as unknown as string[]);
  }, [allNames]);

  const filteredNames = React.useMemo(() => {
    const q = normalize(query.trim());
    if (!q) return allNames;

    return allNames.filter((name) => {
      const tags = frTagsByName[String(name)] ?? [];
      // match "contient"
      return tags.some((t) => t.includes(q));
    });
  }, [allNames, frTagsByName, query]);
  React.useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  return (
    <Popover open={disabled ? false : open} onOpenChange={disabled ? undefined : setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <div
          className={cn(
            'h-14 border bg-white text-tertiary border-input rounded-lg px-4 py-2 flex items-center justify-between cursor-pointer',
            disabled && 'pointer-events-none opacity-60 cursor-not-allowed',
          )}
        >
          {value ? (
            <div className="flex flex-col space-y-0.5">
              <div>
                <p className="text-sm">{t('iconPicker.label')}</p>
              </div>
              <LucideLazyIcon name={value} width={24} height={24} color="black" />
            </div>
          ) : (
            <>
              <p className="text-base">Icône</p>
              <LucideLazyIcon name="Image" width={24} height={24} color="black" />
            </>
          )}
          <ChevronDown
            className={cn(
              'size-6 text-black shrink-0 transition-transform duration-200',
              open && 'rotate-180',
            )}
          />
        </div>
      </PopoverTrigger>

      <PopoverContent className="w-[420px] p-3" align="start">
        {/* ✅ Barre de recherche */}
        <div className="mb-2 space-y-2">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('iconPicker.searchPlaceholder')}
          />

          <p className="text-xs text-muted-foreground">
            {filteredNames.length} résultat{filteredNames.length > 1 ? 's' : ''}
          </p>
          {value && (
            <button
              type="button"
              className="w-full flex items-center gap-2 cursor-pointer"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange(undefined);
                setOpen(false);
              }}
            >
              Désélectionner <X className="size-4 ml-2" />
            </button>
          )}
        </div>

        <ScrollArea className="h-[320px] ">
          <div className="grid grid-cols-8 gap-2">
            {filteredNames.map((name) => (
              <button
                key={String(name)}
                type="button"
                className={[
                  'h-10 w-10 rounded-md border border-input flex items-center justify-center cursor-pointer',
                  'hover:bg-gray-100 hover:text-black',
                  value === name ? 'bg-accent text-accent-foreground' : 'bg-background',
                ].join(' ')}
                title={String(name)}
                onClick={() => {
                  onChange(name);
                  setOpen(false);
                }}
              >
                <LucideLazyIcon name={name} width={24} height={24} />
              </button>
            ))}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
